/**
 * JobsLibraryV2.jsx
 *
 * Ant Design version of Jobs Library.
 * Two view modes toggled from Zustand store (viewByJobCodeOrRequisitionNumber):
 *   "job-code"         → groups by job code with expand/collapse requisition rows
 *   "requisition-number" → flat list of individual requisitions
 *
 * State management: Zustand (useJobsStore / useEntitlementsStore) — no Context re-renders.
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal } from "antd";
import PageHeader from "Components/PageHeader";
import SbButton from "uiCore/SbButton/SbButton";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

// API and Utils
import {
  getJobView,
  getJobRequisitionView,
  getRnCJobView,
  getRnCJobRequisitionView,
  updateCriticalRole,
} from "Api/apiService";
import { buildFieldConfigMap, convertDateFormat } from "Utils/formatting";
import { userHasEntitlement } from "Utils/parseEntitlements";
import { DEEP_LINK_ACTIONS } from "Utils/deepLinks";
import useDeepLinkAction from "Hooks/useDeepLinkAction";
import { EventSourcePolyfill } from "event-source-polyfill";
import { API_ENDPOINTS } from "config/ApiConfig";

// Context and State
import useAuthStore from "Store/useAuthStore";
import analytics, { ANALYTICS_EVENTS } from "Utils/analytics";
import useJobsStore from "Store/useJobsStore";
import useEntitlementsStore from "Store/useEntitlementsStore";
import { showNotification } from "Store/actions/notificationActions";
import { hideToast } from "Store/actions/profileListActions";
import ToasterModal from "Components/SbErrorToasterComponent/SbErrorToaster";

import SbJdCreationDrawerV2 from "Components/SbJdCreationDrawerV2/SbJdCreationDrawerV2";
import Permission from "Components/Permission";
import JobsCodeViewTableV2 from "./SbJobsCodeViewV2";
import RequisitionViewTableV2 from "./SbJobsRequisitionV2";
import JobsLibrarySearchV2 from "./JobsLibrarySearchV2";

// V2 Styled Components
import {
  SearchWrapperStyled,
  DuplicateJdModalGlobal,
  TourTargetStyled,
  ViewByLabelStyled,
  ViewByTextStyled,
} from "./JobsLibraryV2.styles";
import {
  PageContainerStyled,
  TableWrapperStyled,
  ActionRowStyled,
} from "styles/common.styles";
import { SbTooltip, TooltipTriggerStyled } from "uiCore";
import SbSwitch from "uiCore/SbSwitch/SbSwitch";
import { REQ_SEARCHABLE_FIELDS, JOB_CODE_SEARCHABLE_FIELDS, FILTER_KEYS, VIEW_MODES, JD_TABS, JD_STATUSES, COMPARE_ENTRY, dropArchivedJdFamilies } from "constants/jobs.constants";
import { useJdListReload, usePendingJdRefresh } from "Hooks/useJdListRefresh";

// Guided tour — reuses the shared SbTour helpers
import SbTour from "uiCore/SbTour/SbTour";
import SbEmpty from "uiCore/SbEmpty/SbEmpty";
import useModuleTour from "uiCore/SbTour/useModuleTour";
import buildTourSteps from "uiCore/SbTour/buildTourSteps";
import { CheckCircleOutlined } from "uiCore/SbIcons/SbIcons";
import JobslibraryIcon from "Components/SBLayoutIcon/JobslibraryIcon";
import useTourStore from "Store/useTourStore";
import { getTourSteps, TOUR_MODULE_KEY, CROSS_PAGE_TOUR_PHASE, TOUR_COLUMN_CLASS } from "./JobsLibraryV2.constants";


// PRD §5.1 — tooltip content strings for Jobs Library header
const TOOLTIP_CREATE_JD = "Start the AI-powered JD creation process. Takes approximately 4-5 minutes. You'll need the job code, business unit, function, and role details ready.";
const TOOLTIP_COMPARE_JDS = "Compare any two published job descriptions from the library.";
const TOOLTIP_VIEW_TOGGLE = "Switch between Job Code view (grouped by role type) and Requisition view (flat list of all individual JDs with their status).";
const TOOLTIP_SEARCH = "Search across job code, job title, business unit, location, function, sub-function, job level, and version. Case-insensitive.";

// Splits an orgMetaData title into its page name and view-mode label, e.g.
// "Jobs Library - View by Job Code" -> { heading, mode }. Falls back to the
// whole title when a tenant does not use that wording.
const splitViewByTitle = (title) => {
  const [heading, mode] = (title || "").split(/\s*[-–—]\s*view\s+by\s+/i);
  return { heading: heading || "", mode: mode || "" };
};

// ─── Constants ────────────────────────────────────────────────────────────────

// Fields to search when in requisition view (flat list)

const INITIAL_FILTERS = Object.fromEntries(FILTER_KEYS.map((key) => [key, []]));

// Verbatim from the V1 page these events used to fire from (Pages/
// JobsLibrary/SbJobsLibrary.jsx), space and all — `from` is a breakdown
// property on the existing Mixpanel boards, so changing it would split the
// series rather than restore it.
const ANALYTICS_FROM = "jobs library";

const normalize = (val) => (val ?? "--").trim().toLowerCase();

// ─── Component ────────────────────────────────────────────────────────────────

const JobsLibraryV2 = () => {
  // ── Auth (non-entitlement fields still from AuthContext) ───────────────────
  const email = useAuthStore((s) => s.email);
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const accessToken = localStorage.getItem("access_token");
  const { entitlementsData, profileRole } = useEntitlementsStore();

  const location = useLocation();
  const taskId = location?.state?.task_id;

  // ── Zustand jobs store (fine-grained selectors) ────────────────────────────
  const viewByJobCodeOrRequisitionNumber = useJobsStore((state) => state.viewByJobCodeOrRequisitionNumber);
  const setViewByJobCodeOrRequisitionNumber = useJobsStore((state) => state.setViewByJobCodeOrRequisitionNumber);
  const isJobCodeView = viewByJobCodeOrRequisitionNumber === VIEW_MODES.JOB_CODE;
  const isDrawerOpen = useJobsStore((state) => state.isDrawerOpen);
  const toggleDrawer = useJobsStore((state) => state.toggleDrawer);
  const pendingJdId = useJobsStore((state) => state.pendingJdId);
  const setPendingJdId = useJobsStore((state) => state.setPendingJdId);
  const setSelectedJdTab = useJobsStore((state) => state.setSelectedJdTab);
  const setSelectedJob = useJobsStore((state) => state.setSelectedJob);
  const jdActionSelection = useJobsStore((state) => state.jdActionSelection);
  const setJdActionSelection = useJobsStore((state) => state.setJdActionSelection);
  const jobData = useJobsStore((state) => state.jobData);
  const setJobData = useJobsStore((state) => state.setJobData);
  const setCompareViewSelection = useJobsStore((state) => state.setCompareViewSelection);

  // ── Guided tour ────────────────────────────────────────────────────────────
  // Cross-page  (the Job Details Toolbar step lives on the Job Details page).
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const clearCrossPageTourPhase = useTourStore((s) => s.clearCrossPageTourPhase);

  // Shared tour wiring: controller + seen-flag + auto-launch + global-button
  // registration. Auto-launch is suppressed while a cross-page hop is in flight;
  // the resume effect below reopens at Finish.
  const {
    open: tourOpen,
    current: tourStep,
    remountKey: tourRunId,
    begin,
    goToStep: goToTourStep,
    close: closeMainTour,
    markSeen,
  } = useModuleTour({ moduleKey: TOUR_MODULE_KEY, autoLaunch: !crossPageTourPhase });

  const tourTargetRefs = {
    createJd: useRef(null),
    viewToggle: useRef(null),
    search: useRef(null),
    compare: useRef(null),
    table: useRef(null),
    emptyState: useRef(null),
  };

  // ── Local state ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [drawerSubmitLoading, setDrawerSubmitLoading] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [pageSearchTerm, setPageSearchTerm] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [jobTaskId, setJobTaskId] = useState("");
  const [messages, setMessages] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { show: showUploadToast, message: uploadToastMessage, type: uploadToastType } = useSelector((state) => state.profileList.toast);

  // ── Entitlement-gated API selection ───────────────────────────────────────
  const canViewRnC = userHasEntitlement(entitlementsData, profileRole, "jobs_library", "audit");
  const canCreateJd = userHasEntitlement(entitlementsData, profileRole, "jobs_library", "create");

  // ── Deep link: /jobs-library?action=create-jd ──────────────────────────────
  // JD creation is a drawer, not a route, so there is no URL that lands the user
  // inside it. Ask Allie's "Click here" links need one — pointing them at plain
  // /jobs-library drops the user on the list and makes them hunt for the button.
  useDeepLinkAction({
    action: DEEP_LINK_ACTIONS.CREATE_JD,
    ready: entitlementsData != null || !!profileRole,
    allowed: canCreateJd,
    onAllowed: () => {
      if (isDrawerOpen) return;
      setMessages("");
      setJdActionSelection("create");
      toggleDrawer();
    },
    onDenied: () =>
      dispatch(showNotification("You don't have permission to create a job description.", "error")),
  });

  // ── orgMetaData labels ─────────────────────────────────────────────────────
  const jobsLibraryReqTitle = orgMetaData?.ui_elements?.find(el => el.type === 'req_no')?.field_details?.title;
  const jobsLibraryJCTitle  = orgMetaData?.ui_elements?.find(el => el.type === 'job_code')?.field_details?.title;
  const jobsLibraryDescription = orgMetaData?.ui_elements?.find(
    el => el.type === (isJobCodeView ? 'job_code' : 'req_no')
  )?.field_details?.description;

  // Titles arrive as "Jobs Library - View by Job Code". The toggle now shows the
  // mode, so the heading keeps the name and each label takes its own mode.
  const jcTitle = splitViewByTitle(jobsLibraryJCTitle);
  const reqTitle = splitViewByTitle(jobsLibraryReqTitle);
  const jobsLibraryHeading = jcTitle.heading || reqTitle.heading;

  // ── Event handlers ─────────────────────────────────────────────────────────

  const toggleExpand = (jobCode) => {
    setExpandedRow(expandedRow === jobCode ? null : jobCode);
  };

  // Fires only when a suggestion is picked (click, or arrow keys + Enter) and
  // narrows the list to that value. Enter on typed text picks nothing — the
  // search box is an AutoComplete (JobsLibrarySearchV2), so the typed text
  // stays the filter.
  const handleSearchSelect = (value) => {
    setPageSearchTerm(value ?? "");
    if (value) {
      analytics.trackSearchSelectedJds({
        from: ANALYTICS_FROM,
        email,
        searchSelectedJd: value,
        description: ANALYTICS_EVENTS.JOBS_LIBRARY.SEARCH_SELECTED_JDS,
      });
    }
  };

  // 1000ms debounce, matching V1 — antd's onSearch fires per keystroke, so an
  // undebounced track() here would send one event per character typed.
  const handleSearchTyping = (value) => {
    setPageSearchTerm(value);
    if (searchAnalyticsDebounceRef.current) clearTimeout(searchAnalyticsDebounceRef.current);
    if (!value) return;
    searchAnalyticsDebounceRef.current = setTimeout(() => {
      analytics.trackSearchJds({
        from: ANALYTICS_FROM,
        email,
        searchJD: value,
        description: ANALYTICS_EVENTS.JOBS_LIBRARY.SEARCH_JDS,
      });
    }, 1000);
  };

  const handleCheckboxChange = async (event, sbUniqueId) => {
    event.stopPropagation();
    setLoading(true);
    const newState = event.target.checked;
    analytics.trackCriticalStatusFilter({
      from: ANALYTICS_FROM,
      email,
      description: ANALYTICS_EVENTS.JOBS_LIBRARY.CRITICAL_STATUS_FILTER,
    });
    try {
      const response = await updateCriticalRole({ sb_unique_id: sbUniqueId, criticalrole_ischecked: newState });
      if (response?.message) {
        fetchRoles();
      } else {
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
    }
  };

  /**
   * handleRowClick
   *
   * For requisition view: `job` is the flat requisition record, `parentJob` is undefined.
   * For job-code view:    `job` is the requisition_group item, `parentJob` is the parent
   *                       job-code object — we merge parent fields so SbJobDetailsV2 /
   *                       useJobDetails have all the data they need.
   */
  const handleRowClick = (job, parentJob) => {
    if (job.jd_status === JD_STATUSES.PENDING_ANALYSIS) return;

    const mergedJob = parentJob
      ? {
          // parent-level fields (job code group)
          job_code: parentJob.job_code,
          job_title: parentJob.job_title,
          business_unit: parentJob.business_unit,
          job_level: parentJob.job_level,
          legal_entity: parentJob.legal_entity,
          work_environment: parentJob.work_environment,
          role_type: parentJob.role_type,
          // requisition-level fields (override parent where keys conflict)
          ...job,
          sb_requisition_number: job.requisition_id || job.sb_requisition_number,
        }
      : job;

    if (mergedJob?.job_id && mergedJob.job_id === pendingJdId) {
      setPendingJdId("");
    }

    setSelectedJob(mergedJob);
    navigate(`/jobs-library/job-details/${mergedJob.job_id}/${mergedJob.sb_unique_id}`);

    const isApprovePending =
      mergedJob?.jd_status === JD_STATUSES.PENDING_APPROVAL &&
      mergedJob?.assigned_approver_email === email;
    if (isApprovePending) {
      setSelectedJdTab(JD_TABS.JOB_COMPARE);
      setCompareViewSelection("difference");
    }
  };

  // ── API calls ──────────────────────────────────────────────────────────────

  const loadRoles = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const resp = isJobCodeView
        ? canViewRnC
          ? await getRnCJobView(email)
          : await getJobView(email)
        : canViewRnC
        ? await getRnCJobRequisitionView(email)
        : await getJobRequisitionView(email);

      // Drop a response that finished after the user switched views.
      if (isJobCodeViewRef.current !== isJobCodeView) return;
      setJobData(dropArchivedJdFamilies(resp) ?? []);
    } catch (err) {
      console.error("Error fetching roles:", err);
      // A failed quiet refresh keeps the current list.
      if (!silent) setJobData([]);
    } finally {
      setLoading(false);
    }
  };
  const fetchRoles = useJdListReload(loadRoles);

  // ── Computed data ──────────────────────────────────────────────────────────

  const mappedJobData = useMemo(() => {
    if (!jobData?.length) return [];

    // Job-code view: parent items don't need version_info — version lives in requisition_groups
    if (isJobCodeView) return jobData;

    // Requisition view: add version_info string for display + filtering
    return jobData.map((job) => {
      let versionInfo = "N/A";
      if (job?.last_updated_time_stamp) {
        const { date, time } = convertDateFormat(job.last_updated_time_stamp);
        versionInfo = [
          job?.version ? `v${job.version} -` : "",
          date, "at", time, " - ", job?.current_owner || "",
        ].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
      }
      return { ...job, version_info: versionInfo };
    });
  }, [jobData, isJobCodeView]);

  const filteredJobs = useMemo(() => {
    const searchTerm = normalize(pageSearchTerm);
    const activeSearchFields = isJobCodeView ? JOB_CODE_SEARCHABLE_FIELDS : REQ_SEARCHABLE_FIELDS;

    let result = mappedJobData?.filter((job) => {
      if (!searchTerm) return true;
      return activeSearchFields.some(
        (field) => job[field] && typeof job[field] === "string" && normalize(job[field]).includes(searchTerm)
      );
    });

    // Column filters and SSE tracking only apply to the flat requisition view
    if (!isJobCodeView) {
      result = result?.filter((job) =>
        FILTER_KEYS.every(
          (key) => !filters[key].length || filters[key].map(normalize).includes(normalize(job[key]))
        )
      );
    }

    return result;
  }, [mappedJobData, pageSearchTerm, filters, isJobCodeView]);

  // ── Guided tour: branch + handlers ─────────────────────────────────────────
  const isLibraryEmpty = !loading && (jobData?.length ?? 0) === 0;
  // The Job Details Toolbar step shows on the Job Details page (after the
  // detour), not here — exclude it. Advancing into Finish triggers the detour.
  const tourSteps = getTourSteps(isLibraryEmpty).filter((step) => !step.handoffToPage);

  const closeTour = () => {
    markSeen();
    closeMainTour();
  };

  // Finish greeting primary → open the Create JD drawer.
  const finishTour = () => {
    markSeen();
    closeMainTour();
    setMessages("");
    setJdActionSelection("create");
    toggleDrawer();
  };

  // Advancing into Finish (has-data) detours through the Job Details page for the
  // toolbar step: open the first JD; that page navigates back to resume here.
  const changeTourStep = (step) => {
    const goingToFinish = step === tourSteps.length - 1;
    if (goingToFinish && step > tourStep && !isLibraryEmpty && filteredJobs?.length) {
      setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.JOB_DETAILS);
      handleRowClick(filteredJobs[0]);
      return;
    }
    goToTourStep(step);
  };

  // Returning from the Job Details toolbar step → resume at Finish once settled.
  useEffect(() => {
    if (crossPageTourPhase !== CROSS_PAGE_TOUR_PHASE.FINISH || loading) return;
    clearCrossPageTourPhase();
    begin(tourSteps.length - 1); // Finish is the last on-page step
  }, [crossPageTourPhase, loading, tourSteps.length, clearCrossPageTourPhase, begin]);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const eventSourceRef = useRef(null);
  const searchAnalyticsDebounceRef = useRef(null);
  const isJobCodeViewRef = useRef(isJobCodeView);
  isJobCodeViewRef.current = isJobCodeView;

  usePendingJdRefresh(jobData, fetchRoles);

  // A pending search event must not fire after the user has left the page.
  useEffect(() => () => clearTimeout(searchAnalyticsDebounceRef.current), []);

  // ── Effects ────────────────────────────────────────────────────────────────

  // Populate search dropdown from visible data
  useEffect(() => {
    if (!mappedJobData?.length) { setSearchOptions([]); return; }
    const activeFields = isJobCodeView ? JOB_CODE_SEARCHABLE_FIELDS : REQ_SEARCHABLE_FIELDS;
    const uniqueResults = new Set();
    mappedJobData.forEach((job) => {
      activeFields.forEach((field) => {
        const v = job[field];
        if (v && typeof v === "string" && v.trim() !== "--") uniqueResults.add(v);
      });
    });
    setSearchOptions(
      Array.from(uniqueResults)
        .sort((a, b) => {
          const aNum = /^\d/.test(a);
          const bNum = /^\d/.test(b);
          if (aNum && !bNum) return -1;
          if (!aNum && bNum) return 1;
          return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
        })
        .map((value) => ({ value, label: value }))
    );
  }, [mappedJobData, isJobCodeView]);

  useEffect(() => {
    if (taskId) setJobTaskId({ task_id: taskId, job_id: taskId });
  }, [taskId]);

  // SSE listener — PENDING_ANALYSIS tracking (mirrors V1 SbJobsLibrary logic)
  useEffect(() => {
    if (
      !jobTaskId?.task_id ||
      !accessToken ||
      location?.pathname !== "/jobs-library"
    ) return;

    let cancelled = false;   // set true on cleanup — stops pending retries
    let retryCount = 0;      // shared across retries via closure, no ref needed
    const MAX_RETRIES = 3;

    const connectSSE = () => {
      if (cancelled) return;

      const url = `${API_ENDPOINTS.RAPPID_MQ_EVENTS}${jobTaskId?.task_id}`;
      const eventSource = new EventSourcePolyfill(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
        heartbeatTimeout: 60 * 60 * 1000,
      });
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        if (cancelled) return;
        const parseData = JSON.parse(event.data);

        // Backend sends this when Redis stream key not ready yet (race condition).
        // Retry after 2s — button stays loading so user cannot double-submit.
        if (parseData.event === "key_does_not_exist") {
          eventSource.close();
          eventSourceRef.current = null;
          if (drawerSubmitLoading && retryCount < MAX_RETRIES) {
            retryCount++;
            setTimeout(connectSSE, 2000);
          } else {
            setDrawerSubmitLoading(false);
          }
          return;
        }

        setLoading(false);
        setMessages((prev) =>
          Number(parseData.progress) < Number(prev?.progress ?? -1) ? prev : parseData
        );

        if (Number(parseData.progress) === 30) {
          if ("error" in parseData) {
            setMessages(null);
            eventSource.close();
            eventSourceRef.current = null;
            const orgFieldDataConfig = buildFieldConfigMap(orgMetaData, 'create_jd');
            const functionFieldLabel    = orgFieldDataConfig?.function?.label    ?? "Function";
            const subFunctionFieldLabel = orgFieldDataConfig?.sub_function?.label ?? "Sub-Function";
            Modal.warning({
              title: 'JD Already Exists!',
              content: `Try a different ${functionFieldLabel} and ${subFunctionFieldLabel} combination, or find this role in the Jobs Library to revise.`,
              centered: true,
              okText: 'OK',
              className: 'duplicate-jd-modal',
            });
            setDrawerSubmitLoading(false);
          } else {
            setDrawerSubmitLoading(false);
            fetchRoles();
            if (isDrawerOpen) {
              setJdActionSelection("create");
              toggleDrawer();
            }
          }
        }

        if (Number(parseData.progress) === 100) {
          eventSource.close();
          eventSourceRef.current = null;
          setDrawerSubmitLoading(false);
          dispatch(showNotification(`JD ${jdActionSelection === "create" ? "created" : "revised"} successfully`, "success"));
          setPendingJdId("");
          fetchRoles();
        }
      };

      // Reset button loading so it doesn't stay stuck if SSE drops unexpectedly.
      eventSource.onerror = () => {
        if (cancelled) return;
        eventSource.close();
        eventSourceRef.current = null;
        setDrawerSubmitLoading(false);
      };
    };

    connectSSE();

    return () => {
      cancelled = true;  // stops any pending setTimeout retry from firing
      if (eventSourceRef.current) { eventSourceRef.current.close(); eventSourceRef.current = null; }
    };
  }, [jobTaskId?.task_id, accessToken]);

  // Refetch when view mode or entitlements change
  useEffect(() => {
    if (entitlementsData == null && !profileRole) return;
    fetchRoles();
  }, [viewByJobCodeOrRequisitionNumber, entitlementsData, profileRole]);

  // 'job-compare' tab / compareViewSelection leaked into the next JD open.
  useEffect(() => {
    setSelectedJdTab(JD_TABS.JOB_DESCRIPTION);
    setCompareViewSelection('');
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  // Build the AntD Tour steps + renderers from tourSteps. Column steps target the
  // real header cell by class (AntD duplicates the title node); the rest use
  // their element ref. Greeting (Welcome / Finish) steps get their icon/actions.
  const { steps: builtTourSteps, actionsRender: renderTourActions, indicatorsRender: renderTourIndicators } =
    buildTourSteps(tourSteps, {
      resolveTarget: (step) => {
        const columnClass = TOUR_COLUMN_CLASS[step.key];
        return columnClass
          ? document.querySelector(`.${columnClass}`)
          : tourTargetRefs[step.key]?.current;
      },
      greeting: {
        icon: (step, isLast) => (isLast ? <CheckCircleOutlined /> : <JobslibraryIcon />),
        onPrimary: (step, index, isLast) => (isLast ? finishTour() : changeTourStep(index + 1)),
        onSecondary: () => closeTour(),
      },
    });

  return (
    <>
      <ToasterModal
        show={showUploadToast}
        message={uploadToastMessage}
        type={uploadToastType}
        onClose={() => dispatch(hideToast())}
      />
      <DuplicateJdModalGlobal />
      <PageContainerStyled >
        {/* ── Fixed Header ── */}
        <PageHeader
          title={jobsLibraryHeading}
          subtitle={jobsLibraryDescription}
          titleExtra={
            <>
              {/* Toggle: Job Code ↔ Requisition Number */}
              <ViewByLabelStyled>View By:</ViewByLabelStyled>
              <ViewByTextStyled $active={isJobCodeView}>
                {jcTitle.mode || "Job Code"}
              </ViewByTextStyled>
              <SbTooltip content={TOOLTIP_VIEW_TOGGLE}>
                <TooltipTriggerStyled>
                  <TourTargetStyled ref={tourTargetRefs.viewToggle}>
                  <SbSwitch
                   checked={!isJobCodeView}
                   onChange={() => {
                     analytics.trackChangeToggle({
                       from: ANALYTICS_FROM,
                       email,
                       description: ANALYTICS_EVENTS.JOBS_LIBRARY.CHANGE_TOGGLE,
                     });
                     setJobData([]);
                     setExpandedRow(null);
                     setPageSearchTerm("");
                     setViewByJobCodeOrRequisitionNumber(
                       isJobCodeView ? VIEW_MODES.REQUISITION_NUMBER : VIEW_MODES.JOB_CODE
                     );
                   }}
                  />
                  </TourTargetStyled>
                </TooltipTriggerStyled>
              </SbTooltip>
              <ViewByTextStyled $active={!isJobCodeView}>
                {reqTitle.mode || "Role ID"}
              </ViewByTextStyled>
            </>
          }
          actions={
            <>
            {/* Search */}
            <SbTooltip content={TOOLTIP_SEARCH}>
              <SearchWrapperStyled ref={tourTargetRefs.search}>
                <JobsLibrarySearchV2
                  value={pageSearchTerm}
                  options={searchOptions}
                  onType={handleSearchTyping}
                  onPick={handleSearchSelect}
                  onClear={() => setPageSearchTerm("")}
                  placeholder={isJobCodeView ? "Search by job code, title…" : "Search Jobs Library…"}
                />
              </SearchWrapperStyled>
            </SbTooltip>

            {/* Create JD */}
            <Permission resource="jobs_library" actions={["create"]}>
              <ActionRowStyled>
                 Don't see the role you are looking for?
                <SbTooltip content={TOOLTIP_CREATE_JD}>
                  <TooltipTriggerStyled>
                    <TourTargetStyled ref={tourTargetRefs.createJd}>
                    <SbButton
                      buttonType="primary"
                      className="action-btn"
                      onClick={() => {
                        analytics.trackCreateJD({
                          from: ANALYTICS_FROM,
                          email,
                          description: ANALYTICS_EVENTS.JOBS_LIBRARY.CREATE_JD,
                        });
                        setMessages("");
                        setJdActionSelection("create");
                        toggleDrawer();
                      }}
                      disabled={(Number(messages?.progress) > 0 && Number(messages?.progress) < 100)}
                    >
                      Create Job Description
                    </SbButton>
                    </TourTargetStyled>
                  </TooltipTriggerStyled>
                </SbTooltip>
              </ActionRowStyled>
            </Permission>

            {/* Compare JDs */}
            <Permission resource="jobs_library" actions={["compare_data"]}>
              <ActionRowStyled>
                 Compare published job descriptions
                <SbTooltip content={TOOLTIP_COMPARE_JDS}>
                  <TooltipTriggerStyled>
                    <TourTargetStyled ref={tourTargetRefs.compare}>
                    <SbButton
                      buttonType="primary"
                      className="action-btn"
                      disabled={loading}
                      onClick={() => {
                        analytics.trackCompareJDs({
                          from: ANALYTICS_FROM,
                          email,
                          description: ANALYTICS_EVENTS.JOBS_LIBRARY.COMPARE_JDS_FROM_JOB_LISTING,
                        });
                        setSelectedJdTab(JD_TABS.JOB_COMPARE);
                        navigate('/jobs-library/job-details', {
                          state: { viewMode: 'compare', from: COMPARE_ENTRY.JOBS_LIBRARY },
                        });
                      }}
                    >
                      Compare JDs
                    </SbButton>
                    </TourTargetStyled>
                  </TooltipTriggerStyled>
                </SbTooltip>
              </ActionRowStyled>
            </Permission>
            </>
          }
        />

        {/* ── Table ── */}
        <TableWrapperStyled ref={tourTargetRefs.table}>
          {isLibraryEmpty ? (
            <div ref={tourTargetRefs.emptyState}>
              <SbEmpty description="No Job Descriptions yet. Click Create Job Description above to add your first one." />
            </div>
          ) : isJobCodeView ? (
            <JobsCodeViewTableV2
              filteredJobs={filteredJobs}
              loading={loading}
              handleRowClick={handleRowClick}
              handleCheckboxChange={handleCheckboxChange}
              expandedRow={expandedRow}
              toggleExpand={toggleExpand}
            />
          ) : (
            <RequisitionViewTableV2
              filteredJobs={filteredJobs}
              loading={loading}
              setLoading={setLoading}
              handleRowClick={handleRowClick}
              handleCheckboxChange={handleCheckboxChange}
              jobTaskId={jobTaskId}
              messages={messages}
              setJobTaskId={setJobTaskId}
            />
          )}
        </TableWrapperStyled>
      </PageContainerStyled>

      {/* JD Creation Drawer */}
      {isDrawerOpen && (
        <SbJdCreationDrawerV2
          jdActionSelection={jdActionSelection}
          isDrawerOpen={isDrawerOpen}
          toggleDrawer={toggleDrawer}
          setPendingJdId={setPendingJdId}
          setJobTaskId={setJobTaskId}
          drawerSubmitLoading={drawerSubmitLoading}
          setDrawerSubmitLoading={setDrawerSubmitLoading}
        />
      )}

      {/* ── Guided tour ── */}
      <SbTour
        key={tourRunId}
        open={tourOpen}
        current={tourStep}
        onChange={changeTourStep}
        steps={builtTourSteps}
        onClose={closeTour}
        actionsRender={renderTourActions}
        indicatorsRender={renderTourIndicators}
      />
    </>
  );
};

export default JobsLibraryV2;